const SHOPS_MT_SOURCE = 'shops.mt';
const SHOPS_MT_MEDIUM = 'referral';

/**
 * @param {string} url
 * @returns {boolean}
 */
export function isSafeHttpUrl(url) {
  const trimmed = typeof url === 'string' ? url.trim() : '';
  if (trimmed === '') {
    return false;
  }
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Append shops.mt attribution params for outbound retailer links.
 *
 * Retailers can see traffic in analytics as Source/Medium = shops.mt / referral,
 * or via ref=shops.mt in landing-page URLs.
 *
 * @param {string} url
 * @param {{ content?: string, campaign?: string }} [options]
 * @returns {string}
 */
export function withShopsMtOutboundUrl(url, options = {}) {
  const trimmed = typeof url === 'string' ? url.trim() : '';
  if (trimmed === '' || !isSafeHttpUrl(trimmed)) {
    return '';
  }

  /** @type {Record<string, string>} */
  const params = {
    utm_source: SHOPS_MT_SOURCE,
    utm_medium: SHOPS_MT_MEDIUM,
    ref: SHOPS_MT_SOURCE,
  };
  if (options.content) {
    params.utm_content = options.content;
  }
  if (options.campaign) {
    params.utm_campaign = options.campaign;
  }

  try {
    const parsed = new URL(trimmed);
    for (const [key, value] of Object.entries(params)) {
      parsed.searchParams.set(key, value);
    }
    return parsed.toString();
  } catch {
    return '';
  }
}

/**
 * Remove shops.mt outbound attribution query params from a URL.
 *
 * @param {URL} parsed
 */
function stripShopsMtOutboundParams(parsed) {
  if (parsed.searchParams.get('utm_source') === SHOPS_MT_SOURCE) {
    parsed.searchParams.delete('utm_source');
    parsed.searchParams.delete('utm_medium');
    parsed.searchParams.delete('utm_content');
    parsed.searchParams.delete('utm_campaign');
  } else if (parsed.searchParams.get('utm_medium') === SHOPS_MT_MEDIUM) {
    parsed.searchParams.delete('utm_medium');
  }
  if (parsed.searchParams.get('ref') === SHOPS_MT_SOURCE) {
    parsed.searchParams.delete('ref');
  }
}

/**
 * Full product URL for UI (host, path, query) without shops.mt attribution params.
 *
 * @param {string} url
 * @returns {string}
 */
export function productUrlForDisplay(url) {
  const trimmed = typeof url === 'string' ? url.trim() : '';
  if (trimmed === '') {
    return '';
  }
  try {
    const parsed = new URL(trimmed);
    stripShopsMtOutboundParams(parsed);

    let host = parsed.hostname;
    if (host.startsWith('www.')) {
      host = host.slice(4);
    }

    const path =
      parsed.pathname === '/' || parsed.pathname === ''
        ? ''
        : parsed.pathname;
    const search = parsed.search;

    return `${host}${path}${search}`;
  } catch {
    return trimmed;
  }
}
